import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabaseMutationFn, useSupabaseTypedClient } from 'hooks/reactQuery';
import { useToastWrapper } from 'hooks/useToastWrapper';

import { useCurrentUser } from 'auth/CurrentUserProvider';
import { EdgeFunctionName } from 'types/edgeFunctions';
import { TanstackQueryName } from 'types/frontend';

interface AddCommentParams {
  action: 'add';
  roastId: string;
  content: string;
}

interface DeleteCommentParams {
  action: 'delete';
  roastId: string;
  commentId: string;
}

type CommentMutationParams = AddCommentParams | DeleteCommentParams;

interface CommentRoastResponse {
  comment_id?: string;
  comment_count: number;
  deleted?: boolean;
}

export const useRoastCommentMutation = () => {
  const supabase = useSupabaseTypedClient();
  const queryClient = useQueryClient();
  const { id: userId } = useCurrentUser();
  const { toastSuccess, toastError } = useToastWrapper();

  return useMutation<CommentRoastResponse, string, CommentMutationParams>({
    mutationFn: supabaseMutationFn((params) => {
      if (params.action === 'delete') {
        return supabase.functions.invoke<CommentRoastResponse>(EdgeFunctionName.Roast, {
          body: { type: 'comment', action: 'delete', comment_id: params.commentId },
        });
      }
      return supabase.functions.invoke<CommentRoastResponse>(EdgeFunctionName.Roast, {
        body: { type: 'comment', action: 'add', roast_id: params.roastId, content: params.content },
      });
    }),
    onSuccess: (_, params) => {
      queryClient.invalidateQueries({ queryKey: [TanstackQueryName.GetRoastComments, params.roastId] });
      queryClient.invalidateQueries({ queryKey: [TanstackQueryName.GetRoasts, userId] });
      queryClient.invalidateQueries({ queryKey: [TanstackQueryName.GetAllRoasts] });
      queryClient.invalidateQueries({ queryKey: [TanstackQueryName.GetRoastDetail, params.roastId] });
      queryClient.invalidateQueries({ queryKey: [TanstackQueryName.GetProfileRoasts, userId] });

      if (params.action === 'add') {
        toastSuccess('Comment added');
      } else {
        toastSuccess('Comment deleted');
      }
    },
    onError: (error) => {
      toastError(error || 'Failed to manage comment');
    },
  });
};
